/**
 * StreamingContentDisplay 组件
 * 
 * 流式内容显示组件，带打字机效果动画
 * 
 * @module components/projectSettings/StreamingContentDisplay
 * @see Requirements 4.6, 10.7
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FolderIcon,
  BookOpenIcon,
  GlobeAltIcon,
  PencilSquareIcon,
  ListBulletIcon,
  PaintBrushIcon,
  UserGroupIcon,
  DocumentTextIcon,
  SparklesIcon,
  ScissorsIcon
} from '@heroicons/react/24/outline';
import {
  GenerationStepType,
  GENERATION_STEP_NAMES,
  getLabelsByLocale,
  Locale
} from '@packages/global';

/**
 * 步骤图标映射
 */
const STEP_ICONS: Record<GenerationStepType, React.ComponentType<{ className?: string }>> = {
  [GenerationStepType.PROJECT_META]: FolderIcon,
  [GenerationStepType.STORY_SYNOPSIS]: BookOpenIcon,
  [GenerationStepType.WORLDVIEW]: GlobeAltIcon,
  [GenerationStepType.WRITING_STYLE]: PencilSquareIcon,
  [GenerationStepType.EPISODE_OUTLINE]: ListBulletIcon,
  [GenerationStepType.ART_STYLE]: PaintBrushIcon,
  [GenerationStepType.SUBJECT]: UserGroupIcon,
  [GenerationStepType.NOVEL_PARSE]: DocumentTextIcon,
  [GenerationStepType.NOVEL_SPLIT]: ScissorsIcon,
  [GenerationStepType.EPISODE_SCRIPT]: SparklesIcon
};

/**
 * 步骤描述翻译 key 映射
 */
const STEP_DESCRIPTION_KEYS: Record<GenerationStepType, string> = {
  [GenerationStepType.PROJECT_META]: 'streaming.stepDescription.projectMeta',
  [GenerationStepType.STORY_SYNOPSIS]: 'streaming.stepDescription.storySynopsis',
  [GenerationStepType.WORLDVIEW]: 'streaming.stepDescription.worldview',
  [GenerationStepType.WRITING_STYLE]: 'streaming.stepDescription.writingStyle',
  [GenerationStepType.EPISODE_OUTLINE]: 'streaming.stepDescription.episodeOutline',
  [GenerationStepType.ART_STYLE]: 'streaming.stepDescription.artStyle',
  [GenerationStepType.SUBJECT]: 'streaming.stepDescription.subject',
  [GenerationStepType.NOVEL_PARSE]: 'streaming.stepDescription.novelParse',
  [GenerationStepType.NOVEL_SPLIT]: 'streaming.stepDescription.novelSplit',
  [GenerationStepType.EPISODE_SCRIPT]: 'streaming.stepDescription.episodeScript'
};

interface StreamingContentDisplayProps {
  stepType: GenerationStepType;
  content: string;
  isStreaming: boolean;
  projectMeta?: {
    title: string;
    description: string;
  } | null;
}

/**
 * 打字机光标组件
 */
function TypewriterCursor() {
  return (
    <span className="inline-block w-0.5 h-5 bg-primary animate-pulse ml-0.5 align-middle" />
  );
}

/**
 * StreamingContentDisplay 组件
 */
export default function StreamingContentDisplay({
  stepType,
  content,
  isStreaming,
  projectMeta
}: StreamingContentDisplayProps) {
  const { t, i18n } = useTranslation(['projectSettings', 'common']);
  const generationStepNames = getLabelsByLocale(GENERATION_STEP_NAMES, i18n.language as Locale);
  const Icon = STEP_ICONS[stepType];
  const stepName = generationStepNames[stepType];
  const description = t(STEP_DESCRIPTION_KEYS[stepType]);

  // PROJECT_META 步骤特殊显示
  if (stepType === GenerationStepType.PROJECT_META && projectMeta) {
    return (
      <div className="h-full flex flex-col bg-bg-surface rounded-xl border border-border-subtle p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-50">{stepName}</h2>
            <p className="text-xs text-gray-400">{description}</p>
          </div>
        </div>

        {/* 项目信息卡片 */}
        <div className="flex-1 bg-bg-page rounded-lg border border-border-subtle p-6">
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">{t('streaming.projectTitle')}</label>
              <p className="text-xl font-semibold text-gray-50 mt-1">
                {projectMeta.title}
                {isStreaming && <TypewriterCursor />}
              </p>
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">{t('streaming.projectDescription')}</label>
              <p className="text-sm text-gray-200 mt-1 leading-relaxed">
                {projectMeta.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 其他步骤的通用显示
  return (
    <div className="h-full flex flex-col bg-bg-surface rounded-xl border border-border-subtle p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-50">{stepName}</h2>
          <p className="text-xs text-gray-400">{description}</p>
        </div>
        {isStreaming && (
          <div className="ml-auto flex items-center gap-2">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <span className="text-xs text-primary">{t('streaming.generatingStatus')}</span>
          </div>
        )}
      </div>

      {/* 内容区域 */}
      <div className="flex-1 bg-bg-page rounded-lg border border-border-subtle overflow-hidden">
        <div className="h-full p-4 overflow-y-auto">
          {content ? (
            <div className="prose prose-invert prose-sm max-w-none">
              <p className="text-gray-200 whitespace-pre-wrap leading-[1.8]">
                {content}
                {isStreaming && <TypewriterCursor />}
              </p>
            </div>
          ) : isStreaming ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-400">{t('streaming.generatingContent')}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-gray-400">{t('streaming.waitingGeneration')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
